/** Digits a handle is padded to. Longer ids simply run past it. */
const HANDLE_PAD = 4;

/** Canonical handle shape, matched case-insensitively so a lowercased URL still resolves. */
const HANDLE_PATTERN = new RegExp(`^gm-(\\d{${HANDLE_PAD},})$`, 'i');

/**
 * The handle a reader can quote back, and the same string that addresses the brief in a URL.
 *
 * Padded so references read as a consistent shape rather than growing a digit as the table
 * does. Longer ids run past the padding; nothing is cut, because a reference that does not
 * match the record it names is worse than an uneven one.
 */
export const formatBriefHandle = (submissionId: string): string =>
    `GM-${submissionId.padStart(HANDLE_PAD, '0')}`;

/**
 * The handle as it is shown to a reader, labelled.
 *
 * Kept separate from {@see formatBriefHandle} because only the bare handle belongs in a URL,
 * and the two must not drift.
 */
export const formatBriefReference = (submissionId: string): string =>
    `Reference · ${formatBriefHandle(submissionId)}`;

/**
 * Turn a handle from a URL back into the submission id it names, or null if it is not one.
 *
 * Case is forgiven, because people type addresses in lower case. Nothing else is: the handle
 * must round-trip to exactly what {@see formatBriefHandle} would have produced, so `GM-11`
 * and `GM-00011` are rejected rather than quietly resolving. Accepting several spellings
 * would give one brief several addresses, which muddies analytics and caching for no gain.
 */
export const parseBriefReference = (handle: string): string | null => {
    const match = HANDLE_PATTERN.exec(handle.trim());
    if (!match) return null;

    const submissionId = Number(match[1]);
    if (!Number.isSafeInteger(submissionId) || submissionId <= 0) return null;

    const canonical = String(submissionId);
    return formatBriefHandle(canonical) === handle.trim().toUpperCase()
        ? canonical
        : null;
};
